import { Component, ElementRef, EventEmitter, forwardRef, Inject, Input, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { firstValueFrom } from 'rxjs';
import { Permissions } from '../../common/constants';
import { Ticket, TicketStatus } from '../../model';
import { AppService } from '../../service/app.service';
import { AuthenticationService } from '../../service/authentication.service';
import { CustomPropertyService, CustomPropertyType } from '../../service/custom-property.service';
import { FieldService } from '../../service/field.service';
import { TicketService } from '../../service/ticket.service';
import { CustomTableColumn, CustomTableService } from '../../shared/custom-table';
import { ListWidgetV2Component } from '../../shared/list-widget-v2/list-widget-v2.components';
import { ServitlyBadgePipe } from '../../shared/pipe/servitly-badge.pipe';
import { TicketStatusPipe } from '../../shared/pipe/ticket-status.pipe';
import { ErrorUtility } from '../../utility/error-utility';
import { SnackbarService } from '../shared/snackbar.service';
import { WidgetPrintResizeCoordinatorService } from '../shared/widget-print-resize-coordinator.service';
import { AddTicketDialogComponent } from './add-ticket-dialog.component';
import { TicketInfoDialogComponent } from './ticket-info-dialog.component';

@Component({
    selector: 'ticket-list-widget',
    template: require('./ticket-list-widget.component.html'),
    styles: [`mat-button-toggle ::ng-deep .mat-button-toggle-label-content>* { vertical-align: unset; }`]
})
export class TicketListWidgetComponent extends ListWidgetV2Component<Ticket> implements OnInit {

    @Input() thingId: string;

    @Input() eventId: string;

    @Output() ticketSelected = new EventEmitter<Ticket>();

    loaded: boolean;
    error: string;
    tickets: Ticket[] = [];
    length: number;
    displayedColumns: CustomTableColumn[];
    dataSource = new MatTableDataSource<Ticket>([]);
    ticketStatuses: string[] = Object.values(TicketStatus);
    statuses: string[] = [];
    readPermission: boolean;
    showReceivedSubmittedButtons: boolean = true;
    received: boolean = true;
    receivedCount: number;

    constructor(
        @Inject(forwardRef(() => TicketService)) private ticketService: TicketService,
        @Inject(forwardRef(() => AuthenticationService)) authenticationService: AuthenticationService,
        @Inject(forwardRef(() => AppService)) appService: AppService,
        @Inject(forwardRef(() => FieldService)) protected fieldService: FieldService,
        @Inject(forwardRef(() => MatDialog)) dialog: MatDialog,
        @Inject(forwardRef(() => SnackbarService)) snackbarService: SnackbarService,
        @Inject(forwardRef(() => CustomPropertyService)) private customPropertyService: CustomPropertyService,
        @Inject(forwardRef(() => ElementRef)) elementRef: ElementRef,
        @Inject(forwardRef(() => WidgetPrintResizeCoordinatorService)) coordinator: WidgetPrintResizeCoordinatorService
    ) { super(appService, authenticationService, null, null, dialog, snackbarService, null, elementRef, coordinator); }

    ngOnInit(): void {
        this.readPermission = this.authenticationService.hasPermission(Permissions.READ_TICKET);
        if (!this.readPermission) {
            return;
        }
        this.writePermission = this.authenticationService.hasPermission(Permissions.WRITE_TICKET);
        this.sort = this.sort || ['creationTimestamp', 'desc'];
        if (!this.ticketService.canReceiveTickets() || !this.isOnline()) {
            this.received = false;
            this.showReceivedSubmittedButtons = false;
        }
        this.initDisplayedColumns();
        this.getTicketList();
    }

    private initDisplayedColumns(): void {
        this.displayedColumns = [
            CustomTableService.newSimpleColumn('code', 'codeProperty', 'code').withSortField('code'),
            CustomTableService.newDatetimeColumn('creationTimestamp', 'creationDateProperty', 'creationTimestamp', null).withSortField('creationTimestamp'),
            CustomTableService.newSimpleColumn('title', 'titleProperty', 'title').withSortField('title'),
            CustomTableService.newSimpleColumn('customer', 'customerProperty', 'thing.customer.name').withSortField('thing.customer.name'),
            CustomTableService.newSimpleColumn('location', 'locationProperty', 'thing.location.name').withSortField('thing.location.name'),
            CustomTableService.newSimpleColumn('thing', 'thingProperty', 'thing.serialNumber').withSortField('thing.serialNumber')
        ];
        if (this.thingId) {
            this.displayedColumns.splice(3, 3); // remove customer/location/thing columns
        } else if (this.authenticationService.isCustomerUser()) {
            this.displayedColumns.splice(3, 1); // remove customer columns
        }
        const properties = this.customPropertyService.getCustomPropertyDefinitionByType(CustomPropertyType.Ticket).filter(p => p.displayInTicketList).sort((a, b) => {
            if (a.order == b.order) {
                return a.name > b.name ? 1 : -1;
            }
            return a.order - b.order;
        });
        properties.forEach(p => {
            this.displayedColumns.push(CustomTableService.newPipedColumn(p.name, p.label || p.name, 'properties.' + p.name, 'dictionary:{"showIcon": true, "showLabel": true}').withArgument({ property: p }).withSortField('properties.' + p.name));
        });
        const statusPipe = this.authenticationService.isNewStyleManagerEnabled() ? ServitlyBadgePipe : TicketStatusPipe;
        const statusArg = this.authenticationService.isNewStyleManagerEnabled() ? { colorTokenPrefix: 'ticket-status' } : null;
        this.displayedColumns.push(CustomTableService.newPipedColumn('status', 'statusProperty', 'status', statusPipe).withArgument(statusArg).withSortField('status'));
        this.displayedColumns.push(CustomTableService.newUserBalloonColumn('creationUser', '', 'creationUser').withSortField('creationUserId'));
    }

    getTicketList(): void {
        this.ticketService.getTickets(this.pageIndex, this.pageSize, this.sort, this.received, this.advancedSearchBody?.key, this.statuses, this.thingId, this.eventId).then(pagedList => {
            this.tickets = pagedList.content;
            this.updateElementList(pagedList);
            if (this.received) {
                this.updateReceivedCount();
            }
            this.error = null;
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    protected refreshList(data?: any): void {
        this.loaded = false;
        if (data) {
            this.pageIndex = data.pageIndex;
            this.pageSize = data.pageSize;
            this.advancedSearchBody = data.advancedSearchBody;
            this.sort = data.sort;
        }
        this.getTicketList();
    }

    changePage(pageEvent: PageEvent): void {
        this.pageIndex = pageEvent.pageIndex;
        this.getTicketList();
    }

    protected goToDetail(ticket: Ticket): void {
        if (this.ticketSelected.observed) {
            this.ticketSelected.emit(ticket);
            return;
        }
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.width = '600px';
        dialogConfig.autoFocus = false;
        dialogConfig.data = { ticketId: ticket.id, hideEventDetailsButton: this.eventId };
        firstValueFrom(this.dialog.open(TicketInfoDialogComponent, dialogConfig).afterClosed()).then(refresh => {
            if (refresh) {
                this.refreshList();
            }
        });
    }

    protected subscribeToExportServices(): void {
        // unimplemented method
    }

    addTicket(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '400px';
        dialogConfig.autoFocus = false;
        dialogConfig.data = { thingId: this.thingId, eventId: this.eventId };
        firstValueFrom(this.dialog.open(AddTicketDialogComponent, dialogConfig).afterClosed()).then(ticket => {
            if (ticket) {
                this.showSnackbar('ticketCreatedMessage', null, true, 'Open')
                    .then(ref => firstValueFrom(ref.onAction()).then(() => this.goToDetail(ticket)).catch(() => { /* do nothing */ }));
                this.refreshList();
            }
        })
    }

    private updateReceivedCount(): void {
        this.ticketService.getTickets(0, 1, null, true, null, [TicketStatus.OPEN, TicketStatus.IN_PROGRESS, TicketStatus.ON_HOLD]).then(resp => {
            this.receivedCount = resp.totalElements;
        });
    }

}